import { Controller } from '@hotwired/stimulus';

// Row state is one attribute on the row: data-state="editing" | "invalid" | "busy" | absent (idle).
// _cart.scss shows steppers / confirm-cancel / invalid icon / spinner per state, so JS only writes the state.
// Invalid is editing with a value the input's min/step rejects; is-invalid on the input is only Bootstrap's red border.
export default class extends Controller {
  static targets = ['quantity', 'stepper'];

  connect() {
    this.syncQuantityWidth();
  }

  // Invalid keeps every editing behaviour (blur, Enter, Escape), it only swaps confirm for the red icon.
  get editing() {
    return ['editing', 'invalid'].includes(this.element.dataset.state);
  }

  // Browser already applies step/min; do not re-parse the values we rendered.
  step({ params: { dir } }) {
    if (this.cart.busy) return;
    dir > 0 ? this.quantityTarget.stepUp() : this.quantityTarget.stepDown();
    this.submit();
  }

  onInput() {
    this.startEdit();
    this.syncQuantityWidth();
    this.syncValidity();
  }

  startEdit() {
    if (this.element.dataset.state || !this.cart.requestEdit(this)) return;
    this.originalValue = this.quantityTarget.value;
    this.element.dataset.state = 'editing';
  }

  // Confirm/cancel use pointerdown:prevent, so the input never blurs on those buttons.
  blurCommit() {
    if (this.editing) this.commit();
  }

  confirm(event) {
    event.preventDefault();
    this.commit(true);
  }

  cancel(event) {
    event.preventDefault();
    if (!this.editing) return;
    this.quantityTarget.value = this.originalValue;
    this.exitEdit();
    this.quantityTarget.blur();
  }

  commit(report = false) {
    if (!this.editing) return;
    if (this.quantityTarget.value === this.originalValue) {
      this.exitEdit();
      return;
    }
    this.syncValidity();
    if (!this.quantityTarget.validity.valid) {
      // Native step/min blocks requestSubmit with no turbo:submit-end, so busy+spinner would stick.
      if (report) this.quantityTarget.reportValidity();
      return;
    }
    this.submit();
  }

  remove() {
    if (this.cart.busy) return;
    this.quantityTarget.value = 0;
    if (!this.cart.claimSubmit()) return;
    // Trash becomes a loader only after the form accepts submission; siblings only disable.
    this.element.querySelectorAll('.cart-item-delete button').forEach((btn) => {
      btn.innerHTML = '<span class="spinner-border spinner-border-sm text-secondary" role="status" aria-hidden="true"></span>';
    });
    this.element.dataset.state = 'busy';
    this.quantityTarget.form.requestSubmit();
  }

  exitEdit() {
    this.cart.releaseEdit(this);
    delete this.element.dataset.state;
    // Original or unchanged value is valid; this clears is-invalid.
    this.syncValidity();
    this.syncQuantityWidth();
  }

  // Digit count only; padding lives in SCSS with $input-padding-x-sm so the two cannot drift.
  // d-lg-none hides +/- on desktop at 100+ (Bootstrap utility, so the server can render it too).
  syncQuantityWidth() {
    this.quantityTarget.style.setProperty('--qty-ch', Math.max(3, this.quantityTarget.value.length));
    const large = parseInt(this.quantityTarget.value, 10) > 99;
    this.stepperTargets.forEach((el) => el.classList.toggle('d-lg-none', large));
  }

  submit() {
    if (!this.cart.claimSubmit()) return;
    this.element.dataset.state = 'busy';
    this.quantityTarget.form.requestSubmit();
  }

  // Called after exitEdit too: state is gone by then, so only the border class is touched.
  syncValidity() {
    const valid = this.quantityTarget.validity.valid;
    this.quantityTarget.classList.toggle('is-invalid', !valid);
    if (this.editing) this.element.dataset.state = valid ? 'editing' : 'invalid';
  }
}
